import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
      } else if (mode === "login") {
        await login(form.email, form.password); navigate("/app");
      } else {
        await register(form.name, form.email, form.password); navigate("/app");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };
  const startGoogle = () => {
    sessionStorage.setItem("postGoogleRedirect", "/app");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth/callback")}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VkZGluZyUyMHRhYmxlc2NhcGV8ZW58MHx8fHwxNzg1OTYyMTk5fDA&ixlib=rb-4.1.0&q=85"
          alt="Elegant wedding tablescape" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="overline text-white/80">Evermore · Life's Milestones</p>
          <h2 className="font-serif text-5xl leading-tight mt-3">Every celebration,<br />remembered forever.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16" style={{ background: "#FDFBF7" }}>
        <div className="w-full max-w-sm fade-up">
          <Link to="/" data-testid="brand-home" className="flex items-center gap-2 mb-10">
            <Heart size={20} className="text-[#8C9A86]" fill="#8C9A86" />
            <span className="font-serif text-3xl tracking-tight">Evermore</span>
          </Link>
          <h1 className="font-serif text-4xl tracking-tight mb-2" data-testid="login-heading">
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Reset your password" : "Create your account"}
          </h1>
          <p className="text-stone-500 text-sm mb-8" data-testid="login-subtitle">
            {mode === "login" ? "Sign in to your Evermore account." : mode === "forgot" ? "Enter your email and we'll send you a link to choose a new password." : "Start planning your celebration."}
          </p>

          {mode !== "forgot" && (
            <>
              <button type="button" onClick={startGoogle} data-testid="google-signin-btn"
                className="w-full flex items-center justify-center gap-2.5 rounded-full border border-[#E8E6E1] bg-white py-3 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors">
                <GoogleIcon /> Continue with Google
              </button>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-[#E8E6E1]" /><span className="text-xs text-stone-400">or</span><div className="h-px flex-1 bg-[#E8E6E1]" />
              </div>
            </>
          )}

          {mode === "forgot" && sent ? (
            <div className="rounded-lg bg-[#8C9A86]/12 text-[#5f6b59] px-5 py-4 text-sm" data-testid="forgot-sent">
              If an account exists for that email, a reset link is on its way. Check your inbox (and spam) — the link expires in one hour.
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Your name" value={form.name} testid="name-input"
                onChange={(v) => setForm({ ...form, name: v })} />
            )}
            <Field label="Email" type="email" value={form.email} testid="email-input"
              onChange={(v) => setForm({ ...form, email: v })} />
            {mode !== "forgot" && (
              <Field label="Password" type="password" value={form.password} testid="password-input"
                onChange={(v) => setForm({ ...form, password: v })} />
            )}

            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
                className="text-xs text-[#8C9A86] hover:underline">Forgot your password?</button>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-[#C67A7A]">{error}</p>}

            <button type="submit" disabled={loading} data-testid="submit-btn"
              className="w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm font-medium hover:bg-[#8C9A86] transition-colors duration-300 disabled:opacity-50">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : mode === "forgot" ? "Send reset link" : "Create account"}
            </button>
          </form>
          )}

          <p className="text-sm text-stone-500 mt-6">
            {mode === "forgot" ? (
              <button data-testid="back-to-login-btn" onClick={() => switchMode("login")}
                className="text-[#8C9A86] font-medium hover:underline">Back to sign in</button>
            ) : (<>
              {mode === "login" ? "New here?" : "Already have an account?"}{" "}
              <button data-testid="toggle-mode-btn"
                onClick={() => switchMode(mode === "login" ? "register" : "login")}
                className="text-[#8C9A86] font-medium hover:underline">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </>)}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input
        type={type} value={value} required data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20 transition-colors"
      />
    </label>
  );
}
